import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './VerbalLearning.module.css';
import { ArrowBack } from '@mui/icons-material';
import { db } from "../../utils/firebase";
import { doc, setDoc, serverTimestamp, Timestamp } from "@firebase/firestore";
import { getUserId } from "../../utils/userId";
import { generateTestId, setCognitiveTestId, getCognitiveTestSetId } from "../../utils/testId";
import { v4 as uuidv4 } from "uuid";

const words = [
  'Drum', 'Curtain', 'Bell', 'Coffee', 'School', 'Parent', 'Moon', 'Garden', 'Hat', 'Farmer',
  'Nose', 'Turkey', 'Color', 'House', 'River', 'Dest', 'Ranger', 'Bird', 'Shoe', 'Stove',
  'Mountain', 'Glasses', 'Towel', 'Cloud', 'Boat', 'Lamb', 'Gun', 'Pencil', 'Church', 'Fish'
];

const VerbalLearning: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { hospitalId } = useParams();
  const prefix = hospitalId ? `/${hospitalId}` : '';
  const [gameState, setGameState] = useState<'start' | 'playing' | 'results'>('start');
  const [currentQuestion, setCurrentQuestion] = useState(1);
  const [countdown, setCountdown] = useState(180);
  const [score, setScore] = useState(0);
  const [showVolumeCheck, setShowVolumeCheck] = useState(true);
  const [wordCountdown, setWordCountdown] = useState(5);
  const [selectedWords, setSelectedWords] = useState<string[]>([]);
  const [userInput, setUserInput] = useState('');
  const [correctWords, setCorrectWords] = useState<string[]>([]);
  const [enteredWords, setEnteredWords] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);
  const [allWordsRead, setAllWordsRead] = useState(false);

  useEffect(() => {
    if (gameState === 'playing' && showVolumeCheck) {
      const volumeCheckMessage = t('volume_check_message');
      const utterance = new SpeechSynthesisUtterance(volumeCheckMessage);
      window.speechSynthesis.speak(utterance);

      const timer = setTimeout(() => {
        setShowVolumeCheck(false);
        const countdownInterval = setInterval(() => {
          setWordCountdown((prev) => {
            if (prev <= 1) {
              clearInterval(countdownInterval);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [gameState, showVolumeCheck]);

  useEffect(() => {
    if (gameState === 'playing' && wordCountdown === 0) {
      const selectedWords = words.sort(() => 0.5 - Math.random()).slice(0, 15);
      setSelectedWords(selectedWords);
      
      // Calculate total time needed to read all words
      const totalReadingTime = selectedWords.length * 2000; // 2 seconds per word
      
      // Read all words
      selectedWords.forEach((word, index) => {
        setTimeout(() => {
          const utterance = new SpeechSynthesisUtterance(word);
          utterance.rate = 0.8;
          window.speechSynthesis.speak(utterance);
        }, index * 2000);
      });

      // Start the countdown timer after all words are read
      setTimeout(() => {
        const timer = setInterval(() => {
          setCountdown((prev) => {
            if (prev <= 0) {
              clearInterval(timer);
              setGameState('results');
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
        return () => clearInterval(timer);
      }, totalReadingTime + 1000); // Add 1 second buffer after last word
    }
  }, [gameState, wordCountdown]);

  useEffect(() => {
    if (gameState === 'playing' && wordCountdown === 0) {
      // Set allWordsRead to true after all words are read
      const totalReadingTime = selectedWords.length * 2000;
      const timer = setTimeout(() => {
        setAllWordsRead(true);
      }, totalReadingTime + 1000);
      
      return () => clearTimeout(timer);
    }
  }, [gameState, wordCountdown, selectedWords.length]);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [gameState]);

  useLayoutEffect(() => {
    document.documentElement.style.overflow = 'hidden'; // 잠시 스크롤 막음 (깜빡임 방지)
    document.body.style.overflow = 'hidden';
    
    window.scrollTo(0, 0);
    document.body.scrollTop = 0;
    document.documentElement.scrollTop = 0;

    // 0.1초 뒤에 스크롤 다시 품 (자연스럽게)
    const timer = setTimeout(() => {
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  const handleStart = () => {
    setGameState('playing');
  };

  const handleBack = () => {
    navigate(`${prefix}/cognitive/check/new`);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUserInput(e.target.value);
  };

  const handleInputSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (userInput.trim()) {
      setEnteredWords([...enteredWords, userInput.trim()]);
      
      // Check if the entered word is in the selected words list
      const normalizedInput = userInput.trim().toLowerCase();
      const isCorrect = selectedWords.some(word => word.toLowerCase() === normalizedInput);
      
      if (isCorrect && !correctWords.includes(userInput.trim())) {
        setCorrectWords([...correctWords, userInput.trim()]);
        setScore(correctWords.length + 1);
      }
      
      setUserInput('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleInputSubmit(e);
    }
  };

  const saveResultsToFirebase = async () => {
    try {
      const userId = getUserId();
      const testSetId = getCognitiveTestSetId();

      if (!userId || !testSetId) {
        console.error("사용자 ID 또는 테스트 셋 ID를 찾을 수 없습니다.");
        return;
      }

      const docId = `${userId}_${Date.now()}_${uuidv4()}`;
      const responseRef = doc(db, "userCognitiveResponse", docId);

      await setDoc(responseRef, {
        userId,
        testSetId,
        testType: "test3",
        timestamp: Timestamp.now(),
        results: {
          totalScore: score,
        },
      });

      console.log("VerbalLearning 결과 저장됨 (Firebase ID):", docId);
    } catch (error) {
      console.error("VerbalLearning 결과 저장 오류:", error);
    }
  };

  const handleSubmit = () => {
    setGameState('results');
  };

  const handleFinish = () => {
    saveResultsToFirebase();
    navigate(`${prefix}/cognitive/check/new`);
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}m ${remainingSeconds}s`;
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <button className={styles.backButton} onClick={handleBack}>
          <ArrowBack />
        </button>
        <span className={styles.headerText}>{t('check_before_3')}</span>
      </div>

      {gameState === 'start' && (
        <React.Fragment>
        <div className={styles.startScreen}>
            <p className={styles.digitSpanInstructions1}>{t('verabl_learning_instructions_1')}</p>
            <img
              src="/images/verballearing.png"
              className={styles.instructionImage}
            />
            <p className={styles.digitSpanInstructions2}>{t('verabl_learning_instructions_2')}</p>
            <img
              src="/images/verballearning2.png"
              className={styles.instructionImage}
            />
        </div>
        <button className={styles.startButton} onClick={handleStart}>
              {t('start_test_button')}
            </button>
        </React.Fragment>
      )}

      {gameState === 'playing' && (
        <div className={styles.testScreen}>
          {showVolumeCheck ? (
            <div className={styles.volumeCheck}>
              <svg xmlns="http://www.w3.org/2000/svg" width="51" height="40" viewBox="0 0 51 40" fill="none" className={styles.speakerIcon}>
                <path d="M15.3226 9.58084L19.4393 4.44351C20.621 2.96883 23 3.80442 23 5.69417V33.9705C23 35.8955 20.547 36.7092 19.3965 35.1658L15.3226 29.7006H11C6.02944 29.7006 2 25.6712 2 20.7006V18.5808C2 13.6103 6.02944 9.58084 11 9.58084H15.3226Z" stroke="black" strokeWidth="4" strokeLinecap="round"/>
                <path d="M36 12.8809C40.3946 18.3473 39.585 21.4142 36 26.8809" stroke="black" strokeWidth="3" strokeLinecap="round"/>
                <path d="M41 2.88086C52.7188 16.5471 50.56 24.2141 41 37.8809" stroke="black" strokeWidth="3" strokeLinecap="round"/>
              </svg>
            </div>
          ) : wordCountdown > 0 ? (
            <div className={styles.countdown}>
              <p>{wordCountdown}</p>
            </div>
          ) : !allWordsRead ? (
            <div className={styles.listeningMessage}>
              <p>{t('remember_message')}</p>
              <img 
                src="/images/listen.png" 
                alt="Listen" 
                className={styles.speakerIcon}
              />
            </div>
          ) : countdown > 0 ? (
            <>
              <div className={styles.gameInfo}>
                <div className={styles.countdownTimer}>{formatTime(countdown)}</div>
              </div>
              <div className={styles.wordInputBox}>
                <div className={styles.enteredWords}>
                  {enteredWords.length > 0 ? (
                    enteredWords.map((word, index) => (
                      <span 
                        key={index} 
                        className={correctWords.includes(word) ? styles.correctWord : styles.incorrectWord}
                      >
                        {word}{index < enteredWords.length - 1 ? ', ' : ''}
                      </span>
                    ))
                  ) : (
                    <span className={styles.placeholder}></span>
                  )}
                </div>
              </div>
              
              <div className={styles.inputContainer}>
                <input
                  ref={inputRef}
                  type="text"
                  value={userInput}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  placeholder="Enter"
                  className={styles.wordInput}
                />
              </div>
              
              <button onClick={handleSubmit} className={styles.submitButton}>
                {t('submit_button')}
              </button>
            </>
          ) : (
            <div className={styles.listeningMessage}>
              <p>{t('remember_message')}</p>
              <img 
                src="/images/listen.png" 
                alt="Listen" 
                className={styles.speakerIcon}
              />
            </div>
          )}
        </div>
      )}

      {gameState === 'results' && (
        <React.Fragment>
        <div className={styles.resultsScreen}>
          <h2>{t('test_finished')}</h2>
          <div className={styles.resultsSummary}>
            <p>{t('total_success')}: {score}</p>
            <p>{t('words_entered')}: {enteredWords.join(', ')}</p>
          </div>
        </div>
        <button className={styles.finishButton} onClick={handleFinish}>
            {t('finish_button')}
        </button>
        </React.Fragment>
      )}
    </div>
  );
};

export default VerbalLearning; 